import { useEffect, useState } from "react";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { FileText, Send, Trash2, Download, ExternalLink, Copy, Clock, CheckCircle2, RefreshCw, Search } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  signed: { label: "Signé", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  cancelled: { label: "Annulé", cls: "bg-slate-100 text-slate-600 border-slate-300", icon: Trash2 },
};

const KIND_LABEL = { contract: "Contrat", attestation: "Attestation", avenant: "Avenant", generic: "Générique" };

function signUrlFor(token) {
  return `${window.location.origin}/sign/contrat/${token}`;
}

export default function AgentDocs() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");
  const { lastEvent } = useRealtime();

  const load = async () => {
    try { const r = await http.get("/agent-docs"); setItems(r.data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (lastEvent?.type === "agent_doc_changed") load();
  }, [lastEvent]);

  const resend = async (id) => {
    setBusyId(id); setErr("");
    try { await http.post(`/agent-docs/${id}/resend-email`); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusyId(""); }
  };
  const del = async (id) => {
    if (!askDelete("Supprimer ce document ?")) return;
    try { await http.delete(`/agent-docs/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const download = async (d) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent-docs/${d.id}/pdf`, { credentials: "include" });
      if (!resp.ok) throw new Error("Erreur téléchargement");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(d.title||"Document").replace(/ /g,"_")}_${d.agent_snapshot?.last_name||""}${d.status==="signed"?"_signe":""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(String(e.message||e)); }
  };
  const copyLink = async (token) => {
    try { await navigator.clipboard.writeText(signUrlFor(token)); } catch (_) { /* ignore */ }
  };

  const filtered = items.filter(d => {
    const s = `${d.title} ${d.agent_snapshot?.first_name||""} ${d.agent_snapshot?.last_name||""} ${d.agent_snapshot?.email||""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div className="space-y-4" data-testid="agent-docs-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Documents agents à signer</h1>
          <p className="text-sm text-slate-600">Suivez les contrats, attestations et avenants envoyés aux agents pour signature électronique.</p>
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Rechercher…" data-testid="agent-docs-search"
                 className="pl-8 pr-3 py-2 text-sm border border-slate-300 rounded w-64" />
        </div>
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="py-2 px-3 text-left">Document</th>
              <th className="py-2 px-3 text-left">Agent</th>
              <th className="py-2 px-3 text-left">Type</th>
              <th className="py-2 px-3 text-left">Statut</th>
              <th className="py-2 px-3 text-left">Envoyé le</th>
              <th className="py-2 px-3 text-left">Signé le</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-500">Aucun document. Depuis la fiche d'un agent, cliquez sur « Générer document » puis « Envoyer pour signature ».</td></tr>
            ) : filtered.map(d => {
              const s = STATUS_LABEL[d.status] || STATUS_LABEL.pending;
              const Ic = s.icon;
              return (
                <tr key={d.id} className="hover:bg-slate-50" data-testid={`agent-doc-row-${d.id}`}>
                  <td className="py-2 px-3 font-medium"><FileText className="inline h-4 w-4 text-slate-400 mr-2"/>{d.title}</td>
                  <td className="py-2 px-3">
                    <div className="text-sm">{d.agent_snapshot?.first_name} {d.agent_snapshot?.last_name}</div>
                    <div className="text-[11px] text-slate-500">{d.agent_snapshot?.email}</div>
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-600">{KIND_LABEL[d.kind]||d.kind}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-widest ${s.cls}`}>
                      <Ic className="h-3 w-3" /> {s.label}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-600">{(d.created_at||"").slice(0,10)}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{d.signed_at ? d.signed_at.slice(0,10) : "—"}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    {d.status === "pending" && (
                      <>
                        <button onClick={()=>copyLink(d.sign_token)} title="Copier le lien" className="p-1.5 hover:bg-slate-100 rounded" data-testid={`copy-link-${d.id}`}><Copy className="h-4 w-4 text-slate-600"/></button>
                        <a href={signUrlFor(d.sign_token)} target="_blank" rel="noopener noreferrer" title="Ouvrir la page de signature" className="inline-block p-1.5 hover:bg-slate-100 rounded"><ExternalLink className="h-4 w-4 text-slate-600"/></a>
                        <button onClick={()=>resend(d.id)} disabled={busyId===d.id} title="Renvoyer l'email" className="p-1.5 hover:bg-sky-50 rounded disabled:opacity-50" data-testid={`resend-${d.id}`}>
                          {busyId===d.id ? <RefreshCw className="h-4 w-4 animate-spin text-sky-600"/> : <Send className="h-4 w-4 text-sky-600"/>}
                        </button>
                      </>
                    )}
                    <button onClick={()=>download(d)} title={d.status==="signed"?"Télécharger le PDF signé":"Télécharger l'aperçu"} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`download-${d.id}`}><Download className="h-4 w-4 text-slate-600"/></button>
                    <button onClick={()=>del(d.id)} title={delTitle()} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`delete-${d.id}`}><Trash2 className="h-4 w-4 text-rose-600"/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
